import React, { useState, useEffect } from 'react';
import { getPwaWorkspaceConfig } from '../config/workspace-config';
import {
  isStandalone as checkStandalone,
  isIOSSafari as checkIOSSafari,
  isIOSNonSafari as checkIOSNonSafari,
  isAndroid as checkAndroid,
} from '../utils/deviceDetect';

interface InstallScreenProps {
  onContinueToApp: () => void;
}

export const InstallScreen: React.FC<InstallScreenProps> = ({ onContinueToApp }) => {
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [isIosNonSafari, setIsIosNonSafari] = useState(false);
  const [isAndroidDevice, setIsAndroidDevice] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [installedSuccessfully, setInstalledSuccessfully] = useState(false);

  const wsConfig = getPwaWorkspaceConfig();
  const appName = wsConfig.appName || 'Console';
  const logoUrl = wsConfig.branding?.logo || '/icons/icon-192.png';
  const primaryColor = wsConfig.branding?.primaryColor || '#7C3AED';

  useEffect(() => {
    setIsStandalone(checkStandalone());
    setIsIosSafari(checkIOSSafari());
    setIsIosNonSafari(checkIOSNonSafari());
    setIsAndroidDevice(checkAndroid());

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setInstalledSuccessfully(true);
      setIsStandalone(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setInstalledSuccessfully(true);
      }
      setDeferredPrompt(null);
    } catch (err) {
      console.warn('⚠️ [PWA Install]: Error during prompt execution:', err);
    }
  };

  return (
    <div
      className="pwa-tab-scroll"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100%',
        padding: '40px 24px',
        textAlign: 'center',
        background: '#0B0F19',
      }}
    >
      {/* App Icon */}
      <div
        style={{
          width: '88px',
          height: '88px',
          borderRadius: '24px',
          background: `linear-gradient(135deg, ${primaryColor}, #4F46E5)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '40px',
          marginBottom: '20px',
          boxShadow: `0 10px 30px ${primaryColor}55`,
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {logoUrl && logoUrl.startsWith('/') ? (
          <img src={logoUrl} alt={appName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          '📱'
        )}
      </div>

      <h1
        style={{
          fontSize: '24px',
          fontWeight: '900',
          color: '#FFFFFF',
          marginBottom: '8px',
          letterSpacing: '0.5px',
        }}
      >
        {isStandalone || installedSuccessfully
          ? `${appName} Installed`
          : `Install ${appName}`}
      </h1>

      <p
        style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.65)',
          lineHeight: '1.55',
          maxWidth: '320px',
          marginBottom: '28px',
        }}
      >
        {isStandalone || installedSuccessfully
          ? 'You are running in full-screen standalone app mode. Enjoy native speed and offline support!'
          : `Add ${appName} to your device for instant launch, smooth full-screen gameplay, and notifications.`}
      </p>

      {/* Case 1: Standalone / Already Installed */}
      {(isStandalone || installedSuccessfully) && (
        <div
          style={{
            width: '100%',
            maxWidth: '340px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '28px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: '24px' }}>✨</span>
          <div style={{ fontSize: '13px', color: '#10B981', fontWeight: '600' }}>
            Application is installed and ready for full-screen use.
          </div>
        </div>
      )}

      {/* Case 2: iOS Safari Instructions */}
      {!isStandalone && !installedSuccessfully && isIosSafari && (
        <div
          style={{
            width: '100%',
            maxWidth: '340px',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            padding: '20px',
            textAlign: 'left',
            marginBottom: '28px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                background: primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                fontSize: '13px',
                color: '#fff',
                marginRight: '12px',
                flexShrink: 0,
              }}
            >
              1
            </div>
            <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', lineHeight: '1.4' }}>
              Tap the <span style={{ color: '#A78BFA', fontWeight: '800' }}>Share button (⎋)</span> in the Safari bottom toolbar.
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                background: primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                fontSize: '13px',
                color: '#fff',
                marginRight: '12px',
                flexShrink: 0,
              }}
            >
              2
            </div>
            <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', lineHeight: '1.4' }}>
              Scroll down and tap <span style={{ color: '#A78BFA', fontWeight: '800' }}>"Add to Home Screen"</span>.
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                background: primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                fontSize: '13px',
                color: '#fff',
                marginRight: '12px',
                flexShrink: 0,
              }}
            >
              3
            </div>
            <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', lineHeight: '1.4' }}>
              Tap <span style={{ color: '#A78BFA', fontWeight: '800' }}>"Add"</span> in the top right corner.
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                background: primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                fontSize: '13px',
                color: '#fff',
                marginRight: '12px',
                flexShrink: 0,
              }}
            >
              4
            </div>
            <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', lineHeight: '1.4' }}>
              Open <span style={{ color: '#A78BFA', fontWeight: '800' }}>{appName}</span> from your Home Screen!
            </div>
          </div>
        </div>
      )}

      {/* Case 3: iOS Non-Safari (Chrome, Firefox, etc. on iOS) */}
      {!isStandalone && !installedSuccessfully && isIosNonSafari && (
        <div
          style={{
            width: '100%',
            maxWidth: '340px',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            borderRadius: '20px',
            padding: '20px',
            textAlign: 'left',
            marginBottom: '28px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>🧭</span>
            <div style={{ fontSize: '14px', color: '#FBBF24', fontWeight: '800' }}>
              Open in Safari to Install
            </div>
          </div>
          <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', lineHeight: '1.5', margin: 0 }}>
            iOS only supports installing Progressive Web Apps directly through Apple Safari. Please copy this URL and open it in Safari to add {appName} to your Home Screen.
          </p>
        </div>
      )}

      {/* Case 4: Android / Chromium Desktop with BeforeInstallPrompt */}
      {!isStandalone && !installedSuccessfully && deferredPrompt && (
        <button
          onClick={handleNativeInstall}
          className="pwa-primary-btn"
          style={{
            maxWidth: '340px',
            marginBottom: '14px',
            marginTop: 0,
            padding: '16px',
            fontWeight: '900',
            fontSize: '14px',
            letterSpacing: '1px',
          }}
        >
          ⚡️ INSTALL {appName.toUpperCase()} APP
        </button>
      )}

      {/* Case 5: Desktop browser without prompt */}
      {!isStandalone && !installedSuccessfully && !isIosSafari && !isIosNonSafari && !deferredPrompt && !isAndroidDevice && (
        <div
          style={{
            width: '100%',
            maxWidth: '340px',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '28px',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', marginBottom: '6px' }}>
            💻 Desktop Web App
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', lineHeight: '1.4' }}>
            You can install {appName} directly using the install icon in your browser address bar or through the browser menu.
          </div>
        </div>
      )}

      {/* Navigation Button */}
      <button
        onClick={onContinueToApp}
        className={isStandalone || installedSuccessfully ? 'pwa-primary-btn' : 'pwa-secondary-btn'}
        style={{
          maxWidth: '340px',
          marginTop: 0,
          padding: '14px',
          fontWeight: '700',
          fontSize: '13px',
        }}
      >
        {isStandalone || installedSuccessfully ? 'ENTER APPLICATION' : 'CONTINUE IN BROWSER'}
      </button>
    </div>
  );
};
